import React, { memo, useEffect, useState } from 'react';
import { Button } from '@mui/material';

import { Editor, CardWrapper, useHookForm } from '@monorepo/common';

import { ArrowRight as ArrowRightIcon } from '../../../Icons/arrow-right';
import { IEventsStepProps } from '../../interfaceSteps';
import { IStep4Form } from './formInterface';
import { schema } from './schema';
import { ShortDescriptionField } from '../../../ShortDescriptionField';
import { replaceLineBreaks } from '../../../../utils/shortDescription';
import JobDescriptionTitle from '../../components/JobDescriptionTitle';
import { ServerFieldErrors, useServerErrors } from '../../useServerErrors';

interface IStep4Props extends IEventsStepProps {
  defaultData: IStep4Form | null,
  setStep4: (value: IStep4Form) => void,
  serverErrors?: ServerFieldErrors
}

const empty:string = '<p><br></p>';
export const Step4 = memo(({ onBack, onNext, setStep4, defaultData, Nav, serverErrors }:IStep4Props) => {
  const [editorValue, setEditorValue] = useState<string>(defaultData?.jobDescription || empty);
  const [changedFields, setChangedFields] = useState< IStep4Form | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    errors
  } = useHookForm<IStep4Form>({
    schema,
    showChangedFields: (a) => {
      setChangedFields(a);
    }
  });

  useServerErrors(setError, serverErrors);
  const editorChange = (res: string) => {
    setEditorValue(res);
  };

  const submit = (data:IStep4Form) => {
    if (setStep4 && onNext) {
      setStep4(data);
      onNext();
    }
  };

  const goBack = () => {
    if (onBack) {
      if (changedFields && (JSON.stringify(defaultData).toString() !== JSON.stringify(changedFields))) {
        setStep4(changedFields);
      }
      onBack();
    }
  };

  useEffect(() => {
    if (editorValue === '<p><br></p>') {
      setValue('jobDescription', null);
      return;
    }
    setValue('jobDescription', editorValue.trim());
  }, [editorValue]);

  useEffect(() => {
    if (defaultData) {
      setValue('jobDescription', defaultData.jobDescription === '<p><br></p>' ? null : defaultData.jobDescription);
      setValue('shortDescription', defaultData.shortDescription && replaceLineBreaks(defaultData.shortDescription));
    }
  }, []);

  return (
    <CardWrapper classNameForInternalBlock="cpg-step-card">
      <JobDescriptionTitle />
      <form
        data-cy="step4"
        onSubmit={handleSubmit(submit)}
      >
        <Editor
          isRequired
          value={editorValue}
          onChange={editorChange}
          error={errors.jobDescription ? (errors.jobDescription.message as string | null) : null}
          fieldCommentBottom="We recommend including the job description, job requirements, and candidate qualifications as well as some information about the company."
        />

        <ShortDescriptionField
          extraProps={{ ...register('shortDescription') }}
          errors={errors}
        />

        <Nav
          nextDisabled={false}
          onBack={goBack}
          textPreviousStep="← Back"
          nextButton={(
            <Button
              endIcon={(<ArrowRightIcon fontSize="small" />)}
              variant="contained"
              disabled={false}
              type="submit"
              data-cy="nextStep"
            >
              Next step
            </Button>
                )}
        />
      </form>
    </CardWrapper>
  );
});
